import { useState, useRef, useEffect } from "react";
import { explainPlanHumanReadable } from "../../lib/invoice-parser";
import { track } from "../../lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, User, Send, X, MessageCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AskFlexauraProps {
  context: Record<string, any>;
  plans: Array<{ id: string; label: string; breakdown: any; tenureMonths?: number }>;
}

const QUICK_QUESTIONS = [
  "Why this plan?",
  "How do BNPL providers work?", 
  "What are the fees?",
  "How is my eligibility determined?",
  "Can I switch payment methods later?",
  "What happens if I'm not approved?"
];

export default function AskFlexaura({ context, plans }: AskFlexauraProps) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const generateResponse = async (question: string): Promise<string> => {
    // Simulate thinking time
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    
    const topPlan = plans[0];
    const lowerQuestion = question.toLowerCase();

    // Smart response generation based on question content
    if (lowerQuestion.includes("why") && lowerQuestion.includes("plan")) {
      return `Based on your profile, I recommend "${topPlan?.label}" because: ${explainPlanHumanReadable(topPlan)}. This option offers the best balance of approval speed and flexible terms for your situation.`;
    }
    
    if (lowerQuestion.includes("bnpl") || lowerQuestion.includes("work")) {
      return `BNPL (Buy Now, Pay Later) providers like Tabby and Tamara let you split purchases into 3-4 payments, typically with 0% fees if eligible. They're perfect for purchases up to AED 10-20K and offer instant approval. For larger amounts, we connect you with banks and finance partners.`;
    }
    
    if (lowerQuestion.includes("fee")) {
      return `Fees vary by provider and are always shown upfront before you commit. BNPL providers often charge 0% for eligible customers, while bank installments typically have competitive APRs between 5-15%. Extended finance options have custom rates based on your profile and amount.`;
    }
    
    if (lowerQuestion.includes("eligibility") || lowerQuestion.includes("approved")) {
      return `Eligibility depends on several factors: your credit profile, income, existing commitments, and the purchase amount. We check multiple providers simultaneously to maximize your approval chances. Each provider has different criteria, so you might qualify with one even if another declines.`;
    }
    
    if (lowerQuestion.includes("switch") || lowerQuestion.includes("change")) {
      return `Once you select and confirm a payment method, the terms are locked in with that specific provider. However, for future purchases, you can always choose different options based on what's available and suits your needs best at that time.`;
    }
    
    if (lowerQuestion.includes("not approved") || lowerQuestion.includes("declined")) {
      return `If you're not approved by one provider, don't worry! We work with multiple partners and each has different approval criteria. We'll automatically check other options. You can also try a smaller amount or consider adding a co-applicant to increase approval chances.`;
    }

    // Default personalized response
    const responseTemplates = [
      `That's a great question about Flexaura! We make financing accessible by partnering with leading BNPL providers, banks, and finance houses. This means whether you need AED 2,000 or AED 200,000, we can find the right payment solution for you.`,
      `Flexaura simplifies the financing process by showing you all available options in one place. Instead of applying to multiple providers separately, we handle the heavy lifting and present you with personalized offers based on your profile.`,
      `Our marketplace approach means you get competitive rates and terms. We're not tied to any single provider, so we can recommend what truly works best for your situation - whether that's 0% BNPL, low-APR bank installments, or extended finance terms.`
    ];
    
    return responseTemplates[Math.floor(Math.random() * responseTemplates.length)];
  };

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    track("ai_chat_question_asked", { question: content.trim() });

    try {
      const response = await generateResponse(content.trim());
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Error generating response:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: "I'm having trouble processing your question right now. Please try again in a moment.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = (question: string) => {
    handleSendMessage(question);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!open ? (
        <Button
          onClick={() => {
            track("ask_flexaura_clicked");
            setOpen(true);
          }}
          className="rounded-full w-14 h-14 bg-gradient-to-r from-[hsl(var(--flexaura-teal))] to-[hsl(var(--flexaura-purple))] hover:shadow-lg hover:scale-105 transition-all duration-200"
          size="lg"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
      ) : (
        <Card className="w-96 h-[500px] shadow-2xl border-0 bg-card">
          <CardHeader className="pb-3 bg-gradient-to-r from-[hsl(var(--flexaura-teal))] to-[hsl(var(--flexaura-purple))] text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Bot className="w-5 h-5" />
                Ask Flexaura
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-white/90 font-body">
              Get instant answers about financing options
            </p>
          </CardHeader>
          
          <CardContent className="p-0 flex flex-col h-[420px]">
            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Bot className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground font-body mb-4">
                    Hi! I'm here to help explain Flexaura's financing options. Ask me anything!
                  </p>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-body">Try these quick questions:</p>
                    <div className="flex flex-wrap gap-2">
                      {QUICK_QUESTIONS.slice(0, 3).map((question, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="cursor-pointer hover:bg-secondary/80 text-xs"
                          onClick={() => handleQuickQuestion(question)}
                        >
                          {question}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    "flex gap-3 mb-4",
                    message.type === 'user' ? "flex-row-reverse" : "flex-row"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                    message.type === 'user' 
                      ? "bg-[hsl(var(--flexaura-teal))]" 
                      : "bg-[hsl(var(--flexaura-purple))]"
                  )}>
                    {message.type === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div className={cn(
                    "max-w-[75%] p-3 rounded-xl text-sm font-body",
                    message.type === 'user'
                      ? "bg-[hsl(var(--flexaura-teal))] text-white rounded-tr-none"
                      : "bg-secondary text-secondary-foreground rounded-tl-none"
                  )}>
                    {message.content}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[hsl(var(--flexaura-purple))] flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-secondary p-3 rounded-xl rounded-tl-none">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Thinking...
                    </div>
                  </div>
                </div>
              )}
            </ScrollArea>

            {/* Quick Questions (when no messages) */}
            {messages.length === 0 && (
              <div className="px-4 pb-2">
                <ScrollArea className="max-h-20">
                  <div className="flex flex-wrap gap-1">
                    {QUICK_QUESTIONS.slice(3).map((question, index) => (
                      <Badge
                        key={index + 3}
                        variant="outline"
                        className="cursor-pointer hover:bg-muted text-xs mb-1"
                        onClick={() => handleQuickQuestion(question)}
                      >
                        {question}
                      </Badge>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t bg-muted/30">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about financing options..."
                  className="flex-1 text-sm"
                  disabled={isLoading}
                />
                <Button
                  onClick={() => handleSendMessage(inputValue)}
                  disabled={!inputValue.trim() || isLoading}
                  size="sm"
                  className="bg-gradient-to-r from-[hsl(var(--flexaura-teal))] to-[hsl(var(--flexaura-purple))] hover:opacity-90"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}